import React, { useState, useEffect, useRef } from "react";
import { Bug, Shield, Eye, EyeOff, Loader, RefreshCw, Cloud, Globe, MessageCircle, Plus, Trash2, Save, ToggleLeft, ToggleRight, AlertTriangle, CheckCircle, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Navigation from "../components/Navigation";
import { useApi } from "../hooks/useApi";
import { useRBAC } from "../hooks/useRBAC";
import { useModuleAvailability } from "../hooks/useModuleAvailability";
import UnavailableNote from "./common/UnavailableNote";
import CveSettingsPanel from "./CveSettingsPanel";

export default function SuperConfig() {
  const apiCall = useApi();
  const navigate = useNavigate();
  const { isSuperAdmin } = useRBAC();
  // Platform Config is super-admin-only (nav + route guard enforce this). Super admins
  // can edit everything here — including the org-scoped Telegram sub-sections — without
  // needing a separate ADMIN@ORG role.
  const isAdmin = isSuperAdmin();

  useEffect(() => {
    if (!isSuperAdmin()) {
      navigate("/dashboard");
    }
  }, [isSuperAdmin, navigate]);

  const [scannerLoading, setScannerLoading] = useState(true);
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState(null);
  const [success, setSuccess]     = useState(false);

  // Top-level tabs
  const [mainTab, setMainTab] = useState("scanner");
  // Surface Scan (Scanner & EASM) and Telegram monitoring need internet access: on Restricted /
  // AirGapped installations their tabs are hidden and their endpoints are never called. Wait for
  // the flags so nothing fires early. The effective tab is derived: an unavailable tab falls back
  // to the other one, or to none (the page then says the configuration is not available).
  const { loaded: flagsLoaded, isModuleAvailable } = useModuleAvailability();
  const scannerAvailable = flagsLoaded && isModuleAvailable("surfaceScan");
  const telegramAvailable = flagsLoaded && isModuleAvailable("telegramMonitoring");
  // CVE reads Microsoft Defender (a vendor API): available wherever Defender XDR is, also where public feeds are blocked.
  const cveAvailable = flagsLoaded && isModuleAvailable("defenderXdr");
  const tabAvailable = { scanner: scannerAvailable, telegram: telegramAvailable, cve: cveAvailable };
  const currentTab = tabAvailable[mainTab]
    ? mainTab
    : (scannerAvailable ? "scanner" : (telegramAvailable ? "telegram" : (cveAvailable ? "cve" : null)));

  // Scanner sub-tab
  const [scannerTab, setScannerTab] = useState("shodan");

  // Shodan
  const [shodanKey, setShodanKey]       = useState("");
  const [showKey, setShowKey]           = useState(false);
  const [shodanKeySet, setShodanKeySet] = useState(false);

  // Netlas
  const [netlasKey, setNetlasKey]         = useState("");
  const [showNetlasKey, setShowNetlasKey] = useState(false);
  const [netlasKeySet, setNetlasKeySet]   = useState(false);

  // EASM
  const [easmEnabled, setEasmEnabled]               = useState(false);
  const [overrideEasmWorkspace, setOverrideEasmWorkspace] = useState(false);
  const [easmResourceGroup, setEasmResourceGroup]   = useState("");
  const [easmWorkspaceName, setEasmWorkspaceName]   = useState("");
  const [easmStatus, setEasmStatus]                 = useState(null);
  const [testingEasm, setTestingEasm]               = useState(false);
  const [msConn, setMsConn]                         = useState(null);   // from /security-insights/connection

  // EASM orphaned discovery-group cleanup (fleet-wide maintenance action)
  const [cleaningOrphans, setCleaningOrphans]       = useState(false);
  const [orphanCleanupResult, setOrphanCleanupResult] = useState(null);

  // Telegram — Key Vault credentials
  const [tgSaving, setTgSaving]         = useState(false);
  const [tgSaved, setTgSaved]           = useState(false);
  const [tgError, setTgError]           = useState(null);
  const [tgApiIdSet, setTgApiIdSet]     = useState(false);
  const [tgApiHashSet, setTgApiHashSet] = useState(false);
  const [tgPhoneSet, setTgPhoneSet]     = useState(false);
  const [tgAesKeySet, setTgAesKeySet]   = useState(false);
  const [tgApiId, setTgApiId]           = useState("");
  const [tgApiHash, setTgApiHash]       = useState("");
  const [tgPhone, setTgPhone]           = useState("");
  const [showTgHash, setShowTgHash]     = useState(false);

  // Telegram — Monitoring config
  const [tgMonSaving, setTgMonSaving]   = useState(false);
  const [tgMonError, setTgMonError]     = useState(null);
  const [tgMonSuccess, setTgMonSuccess] = useState(false);
  const [isEnabled, setIsEnabled]       = useState(false);
  const [retentionDays, setRetentionDays] = useState(30);
  const [channels, setChannels]         = useState([]);
  const [keywords, setKeywords]         = useState([]);
  const [newChannel, setNewChannel]     = useState("");
  const [newKeyword, setNewKeyword]     = useState("");

  // Telegram — Channel Reputation
  const [channelReputation, setChannelReputation] = useState([]);
  const [newRepChannel, setNewRepChannel]         = useState("");
  const [newRepCategory, setNewRepCategory]       = useState("generic");
  const [newRepNotes, setNewRepNotes]             = useState("");
  const [addingChannel, setAddingChannel]         = useState(false);
  const [deletingId, setDeletingId]               = useState(null);

  // Telegram — Global keywords (platform-level, applies to all global channels)
  const [globalKeywords, setGlobalKeywords]       = useState([]);
  const [newGlobalKeyword, setNewGlobalKeyword]   = useState("");
  const [globalKwSaving, setGlobalKwSaving]       = useState(false);

  // Telegram — Session management
  const [clearingSession, setClearingSession] = useState(false);
  const [clearSessionMsg, setClearSessionMsg] = useState(null);

  // Telegram — Auth status (code submission)
  const [authStatus, setAuthStatus]           = useState({ awaitingCode: false, codeType: null, isConnected: false, requestId: null });
  const [pendingCode, setPendingCode]         = useState("");
  const [submittingCode, setSubmittingCode]   = useState(false);
  const [codeError, setCodeError]             = useState(null);
  const [codeSuccess, setCodeSuccess]         = useState(false);
  const [lastSubmittedRequestId, setLastSubmittedRequestId] = useState(null);
  // The request a resend was asked from: a newer request after it carries the SMS or call code.
  const [resendFromRequestId, setResendFromRequestId] = useState(null);
  const [resendingCode, setResendingCode]     = useState(false);
  const pollRef                               = useRef(null);

  // Poll auth-status every 5 seconds while on the Telegram tab
  useEffect(() => {
    if (currentTab !== "telegram") {
      clearInterval(pollRef.current);
      return;
    }
    const poll = async () => {
      try {
        const s = await apiCall("GET", "/telegram/auth-status").catch(() => null);
        if (s) setAuthStatus(s);
      } catch { /* silent */ }
    };
    poll();
    pollRef.current = setInterval(poll, 5000);
    return () => clearInterval(pollRef.current);
  }, [currentTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmitCode = async () => {
    const cleanCode = pendingCode.replace(/[\s\-]/g, "");
    if (!cleanCode) return;
    setSubmittingCode(true); setCodeError(null); setCodeSuccess(false);
    try {
      await apiCall("POST", "/telegram/submit-code", { code: cleanCode });
      setLastSubmittedRequestId(authStatus.requestId);
      setPendingCode("");
      setCodeSuccess(true);
      setTimeout(() => setCodeSuccess(false), 4000);
    } catch (e) {
      setCodeError(e?.response?.data?.error ?? "Failed to submit code.");
    } finally { setSubmittingCode(false); }
  };

  // The first code goes to the Telegram app, which reaches no one when no device is signed in.
  const handleResendCode = async () => {
    setResendingCode(true); setCodeError(null); setCodeSuccess(false);
    try {
      await apiCall("POST", "/telegram/resend-code");
      setResendFromRequestId(authStatus.requestId);
      setLastSubmittedRequestId(null);
      setPendingCode("");
    } catch (e) {
      setCodeError(e?.response?.data?.error ?? "Couldn't ask Telegram to send the code again.");
    } finally { setResendingCode(false); }
  };

  // Scanner & EASM configuration, loaded only when Surface Scan is available.
  useEffect(() => {
    if (!scannerAvailable) return;
    (async () => {
      setScannerLoading(true);
      try {
        const [data, conn] = await Promise.all([
          apiCall("GET", "/esm/config").catch(() => null),
          apiCall("GET", "/security-insights/connection").catch(() => null),
        ]);

        setMsConn(conn);
        if (data) {
          setShodanKeySet(data.shodanApiKeySet ?? false);
          setNetlasKeySet(data.netlasApiKeySet ?? false);
          setEasmEnabled(data.easmEnabled ?? false);
          // Override is "on" iff the org has a stored bring-your-own workspace; otherwise it uses the platform default.
          setOverrideEasmWorkspace(!!(data.easmResourceGroup || data.easmWorkspaceName));
          setEasmResourceGroup(data.easmResourceGroup || "");
          setEasmWorkspaceName(data.easmWorkspaceName || "");
        }
      } catch { setError("Failed to load configuration."); }
      finally { setScannerLoading(false); }
    })();
  }, [scannerAvailable]); // eslint-disable-line react-hooks/exhaustive-deps

  // Telegram configuration, loaded only when the Telegram module is available.
  useEffect(() => {
    if (!telegramAvailable) return;
    (async () => {
      try {
        const [tgData, tgCfg, tgChans, tgGlobal] = await Promise.all([
          apiCall("GET", "/telegram/platform-config").catch(() => null),
          apiCall("GET", "/telegram/config").catch(() => null),
          apiCall("GET", "/telegram/channels").catch(() => []),
          apiCall("GET", "/telegram/global-config").catch(() => null),
        ]);

        if (tgData) {
          setTgApiIdSet(tgData.apiIdSet ?? false);
          setTgApiHashSet(tgData.apiHashSet ?? false);
          setTgPhoneSet(tgData.phoneNumberSet ?? false);
          setTgAesKeySet(tgData.aesKeySet ?? false);
        }

        if (tgCfg) {
          setIsEnabled(tgCfg.isEnabled ?? false);
          setRetentionDays(tgCfg.retentionDays ?? 30);
          setChannels(tgCfg.allowedChannels ?? []);
          setKeywords(tgCfg.keywords ?? []);
        }

        setChannelReputation(tgChans ?? []);
        if (tgGlobal) setGlobalKeywords(tgGlobal.keywords ?? []);
      } catch { setError("Failed to load configuration."); }
    })();
  }, [telegramAvailable]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = async () => {
    // When bringing your own EASM workspace, tenant/subscription come from the org's Microsoft
    // connection and both workspace coordinates are required. The platform-default path needs neither.
    if (easmEnabled && overrideEasmWorkspace) {
      if (!easmResourceGroup.trim() || !easmWorkspaceName.trim()) {
        setError("Resource Group and Workspace Name are required when overriding the EASM workspace.");
        return;
      }
      if (!msConn?.tenantId) {
        setError("A Microsoft connection is required to override the EASM workspace. Set it up in Settings → Integrations.");
        return;
      }
    }
    setSaving(true); setError(null); setSuccess(false);
    try {
      // Re-fetch immediately before saving rather than relying on the load-time snapshot, so a save
      // doesn't revert domains/settings changed elsewhere since this page was opened. An org with no
      // Surface Scan config yet gets an empty 204 body: nothing to overwrite, so save with defaults.
      let latest;
      try { latest = (await apiCall("GET", "/esm/config")) || {}; }
      catch {
        setError("Failed to load current configuration — save aborted to avoid overwriting domains.");
        return;
      }
      const payload = {
        domains: latest.domains || [],
        isEnabled: latest.isEnabled ?? true,
        scanFrequencyHours: latest.scanFrequencyHours || 24,
        msEnabled: latest.msEnabled ?? false,
        ransomwareMonitoringEnabled: latest.ransomwareMonitoringEnabled ?? true,
        easmEnabled,
        overrideEasmWorkspace,
        easmResourceGroup: overrideEasmWorkspace ? (easmResourceGroup.trim() || null) : null,
        easmWorkspaceName: overrideEasmWorkspace ? (easmWorkspaceName.trim() || null) : null,
        ...(shodanKey.trim() ? { shodanApiKey: shodanKey.trim() } : {}),
        ...(netlasKey.trim() ? { netlasApiKey: netlasKey.trim() } : {}),
      };
      await apiCall("POST", "/esm/config", payload);
      setShodanKey("");
      setShodanKeySet(!!shodanKey.trim() || shodanKeySet);
      setNetlasKey("");
      setNetlasKeySet(!!netlasKey.trim() || netlasKeySet);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch { setError("Failed to save configuration."); }
    finally { setSaving(false); }
  };

  const handleTestEasm = async () => {
    setTestingEasm(true); setEasmStatus(null);
    try {
      const result = await apiCall("GET", "/esm/easm-status");
      setEasmStatus(result);
    } catch (e) { setEasmStatus({ ok: false, reason: String(e) }); }
    finally { setTestingEasm(false); }
  };

  // Fleet-wide sweep: deletes EASM discovery groups no org still seeds. Backend reference-counts
  // across every org, so a domain shared by another org is never removed.
  const handleCleanupOrphans = async () => {
    if (!window.confirm(
      "Delete Microsoft Defender EASM discovery groups that no organization uses anymore?\n\n" +
      "Groups for domains still configured by any org (including other orgs) are kept. This does not " +
      "remove already-discovered assets from EASM inventory."
    )) return;
    setCleaningOrphans(true); setOrphanCleanupResult(null);
    try {
      const result = await apiCall("POST", "/superadmin/easm/orphan-cleanup");
      setOrphanCleanupResult(result);
    } catch (e) {
      setOrphanCleanupResult({ ran: false, error: String(e?.response?.data?.message || e) });
    } finally { setCleaningOrphans(false); }
  };

  const handleClearSession = async () => {
    if (!window.confirm("Clear the saved Telegram session? Telegram logs in again within a few seconds and asks for a new login code.")) return;
    setClearingSession(true); setClearSessionMsg(null);
    try {
      await apiCall("DELETE", "/telegram/session");
      setClearSessionMsg({ ok: true, text: "Session cleared. Telegram is logging in again; enter the login code in the box above when it appears." });
      setAuthStatus(s => ({ ...s, isConnected: false }));
    } catch {
      setClearSessionMsg({ ok: false, text: "Failed to clear session." });
    } finally { setClearingSession(false); }
  };

  const handleTelegramSave = async () => {
    setTgSaving(true); setTgError(null); setTgSaved(false);
    try {
      await apiCall("POST", "/telegram/platform-config", {
        apiId: tgApiId || null, apiHash: tgApiHash || null,
        phoneNumber: tgPhone || null,
      });
      if (tgApiId)   setTgApiIdSet(true);
      if (tgApiHash) setTgApiHashSet(true);
      if (tgPhone)   setTgPhoneSet(true);
      setTgApiId(""); setTgApiHash(""); setTgPhone("");
      setTgSaved(true);
      setTimeout(() => setTgSaved(false), 3000);
    } catch { setTgError("Failed to save Telegram credentials."); }
    finally { setTgSaving(false); }
  };

  const handleMonitoringSave = async () => {
    setTgMonSaving(true); setTgMonError(null); setTgMonSuccess(false);
    try {
      await apiCall("PUT", "/telegram/config", {
        isEnabled,
        retentionDays: Math.max(30, Math.min(90, retentionDays)),
        allowedChannels: channels,
        keywords,
      });
      setTgMonSuccess(true);
      setTimeout(() => setTgMonSuccess(false), 3000);
    } catch { setTgMonError("Failed to save monitoring configuration."); }
    finally { setTgMonSaving(false); }
  };

  const addChannel = () => {
    const val = newChannel.trim().toLowerCase().replace(/^@/, "");
    if (!val || channels.includes(val)) return;
    setChannels(prev => [...prev, val]);
    setNewChannel("");
  };

  const removeChannel = (ch) => setChannels(prev => prev.filter(c => c !== ch));

  const addKeyword = () => {
    const val = newKeyword.trim();
    if (!val || keywords.includes(val)) return;
    setKeywords(prev => [...prev, val]);
    setNewKeyword("");
  };

  const removeKeyword = (kw) => setKeywords(prev => prev.filter(k => k !== kw));

  const handleAddRepChannel = async () => {
    if (!newRepChannel.trim()) return;
    setAddingChannel(true);
    try {
      const created = await apiCall("POST", "/telegram/channels", {
        channelName: newRepChannel.trim().toLowerCase().replace(/^@/, ""),
        riskCategory: newRepCategory,
        isApproved: true,
        verifiedPublic: true,
        notes: newRepNotes.trim() || null,
      });
      setChannelReputation(prev => [...prev, created]);
      setNewRepChannel(""); setNewRepNotes(""); setNewRepCategory("generic");
    } catch { setTgMonError("Failed to add channel."); }
    finally { setAddingChannel(false); }
  };

  const handleDeleteRepChannel = async (id) => {
    setDeletingId(id);
    try {
      await apiCall("DELETE", `/telegram/channels/${id}`);
      setChannelReputation(prev => prev.filter(c => c.telegramChannelId !== id));
    } catch { setTgMonError("Failed to delete channel."); }
    finally { setDeletingId(null); }
  };

  const addGlobalKeyword = () => {
    const val = newGlobalKeyword.trim().toLowerCase();
    if (!val || globalKeywords.includes(val)) return;
    setGlobalKeywords(prev => [...prev, val]);
    setNewGlobalKeyword("");
  };

  const removeGlobalKeyword = (kw) => setGlobalKeywords(prev => prev.filter(k => k !== kw));

  const handleGlobalKeywordSave = async () => {
    setGlobalKwSaving(true);
    try {
      await apiCall("PUT", "/telegram/global-config", { keywords: globalKeywords });
    } catch { setTgMonError("Failed to save global keywords."); }
    finally { setGlobalKwSaving(false); }
  };

  const categoryColor = (cat) => {
    const map = { fraud: "bg-red-100 text-red-700", malware: "bg-orange-100 text-orange-700", leaks: "bg-yellow-100 text-yellow-700", generic: "bg-gray-100 text-gray-600" };
    return map[cat] ?? map.generic;
  };

  // Spinner until the flags load, then while the Scanner & EASM configuration loads (if offered).
  const loading = !flagsLoaded || (scannerAvailable && scannerLoading);

  if (loading) return (
    <div className="min-h-screen bg-theme-primary">
      <Navigation />
      <div className="flex justify-center py-16"><Loader className="h-7 w-7 animate-spin text-purple-700" /></div>
    </div>
  );

  return (
    <div className="min-h-screen bg-theme-primary">
      <Navigation />
      <div className="max-w-3xl mx-auto py-8 px-4 sm:px-6 lg:px-8">

        {/* Header */}
        <div className="mb-6 flex items-center gap-3">
          <div className="p-2.5 bg-purple-100 rounded-xl">
            <Shield className="h-6 w-6 text-purple-700" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-content-primary">Platform Scanner Configuration</h1>
            <p className="text-sm text-content-secondary mt-0.5">
              Axaris internal settings — scanner credentials and EASM workspace. Not visible to clients.
            </p>
          </div>
          <span className="ml-auto inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-purple-100 text-purple-700">
            Axaris Only
          </span>
        </div>

        {currentTab === null && (
          <div className="bg-theme-secondary rounded-xl border border-border p-6">
            <UnavailableNote className="text-sm" />
          </div>
        )}

        {/* Top-level tabs */}
        {currentTab !== null && (
        <div className="flex gap-1 p-1 bg-theme-secondary rounded-lg border border-border mb-6 w-fit">
          {scannerAvailable && (
          <button
            onClick={() => setMainTab("scanner")}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-colors ${
              currentTab === "scanner"
                ? "bg-purple-700 text-white shadow-sm"
                : "text-content-secondary hover:text-content-primary"
            }`}
          >
            <Globe className="h-4 w-4" />
            Scanner & EASM
          </button>
          )}
          {telegramAvailable && (
          <button
            onClick={() => setMainTab("telegram")}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-colors ${
              currentTab === "telegram"
                ? "bg-blue-600 text-white shadow-sm"
                : "text-content-secondary hover:text-content-primary"
            }`}
          >
            <MessageCircle className="h-4 w-4" />
            Telegram
            {(tgApiIdSet || tgApiHashSet) && (
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-green-400" />
            )}
          </button>
          )}
          {cveAvailable && (
          <button
            onClick={() => setMainTab("cve")}
            data-testid="superconfig-tab-cve"
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-colors ${
              currentTab === "cve"
                ? "bg-purple-700 text-white shadow-sm"
                : "text-content-secondary hover:text-content-primary"
            }`}
          >
            <Bug className="h-4 w-4" />
            CVE
          </button>
          )}
        </div>
        )}

        {/* ── CVE tab ─────────────────────────────────────────────── */}
        {currentTab === "cve" && <CveSettingsPanel />}

        {/* ── Scanner & EASM tab ─────────────────────────────────── */}
        {currentTab === "scanner" && (
          <>
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>
            )}
            {success && (
              <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">
                Configuration saved successfully.
              </div>
            )}

            {/* Port Scanner */}
            <div className="bg-theme-secondary rounded-xl border border-border p-6 mb-5">
              <div className="flex items-center gap-2 mb-4">
                <Globe className="h-5 w-5 text-purple-700" />
                <h2 className="text-base font-semibold text-content-primary">Port Scanner Configuration</h2>
              </div>

              <div className="flex gap-1 p-1 bg-theme-primary rounded-lg border border-border mb-5 w-fit">
                <button
                  onClick={() => setScannerTab("shodan")}
                  className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
                    scannerTab === "shodan"
                      ? "bg-purple-700 text-white shadow-sm"
                      : "text-content-secondary hover:text-content-primary"
                  }`}
                >
                  Shodan
                  {shodanKeySet && (
                    <span className="ml-1.5 inline-block w-1.5 h-1.5 rounded-full bg-green-400 align-middle" />
                  )}
                </button>
                <button
                  onClick={() => setScannerTab("netlas")}
                  className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
                    scannerTab === "netlas"
                      ? "bg-purple-700 text-white shadow-sm"
                      : "text-content-secondary hover:text-content-primary"
                  }`}
                >
                  Netlas
                  {netlasKeySet && (
                    <span className="ml-1.5 inline-block w-1.5 h-1.5 rounded-full bg-green-400 align-middle" />
                  )}
                </button>
              </div>

              {scannerTab === "shodan" && (
                <>
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-sm font-semibold text-content-primary">Shodan API Key</h3>
                    {shodanKeySet && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Key saved
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-content-secondary mb-4">
                    Shodan membership plan ($69/mo) unlocks open port scanning on all monitored domains.
                    This key is used platform-wide for all org scans.
                  </p>
                  <div className="relative max-w-lg">
                    <input
                      type={showKey ? "text" : "password"}
                      value={shodanKey}
                      onChange={e => setShodanKey(e.target.value)}
                      placeholder={shodanKeySet ? "••••••••• (enter new key to replace)" : "Enter Shodan API key"}
                      disabled={!isAdmin}
                      className="w-full rounded-md border border-border bg-theme-primary text-content-primary shadow-sm sm:text-sm px-3 py-2 pr-10 focus:border-purple-500 focus:ring-purple-500"
                    />
                    <button type="button" onClick={() => setShowKey(p => !p)}
                      className="absolute right-3 top-2.5 text-content-secondary hover:text-content-primary">
                      {showKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </>
              )}

              {scannerTab === "netlas" && (
                <>
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-sm font-semibold text-content-primary">Netlas API Key</h3>
                    {netlasKeySet && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Key saved
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-content-secondary mb-4">
                    Netlas (netlas.io) scans monitored domains for exposed ports. Complements or replaces Shodan —
                    configure either or both. Duplicate findings are automatically merged.
                  </p>
                  <div className="relative max-w-lg">
                    <input
                      type={showNetlasKey ? "text" : "password"}
                      value={netlasKey}
                      onChange={e => setNetlasKey(e.target.value)}
                      placeholder={netlasKeySet ? "••••••••• (enter new key to replace)" : "Enter Netlas API key"}
                      disabled={!isAdmin}
                      className="w-full rounded-md border border-border bg-theme-primary text-content-primary shadow-sm sm:text-sm px-3 py-2 pr-10 focus:border-purple-500 focus:ring-purple-500"
                    />
                    <button type="button" onClick={() => setShowNetlasKey(p => !p)}
                      className="absolute right-3 top-2.5 text-content-secondary hover:text-content-primary">
                      {showNetlasKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </>
              )}
            </div>

            {/* EASM */}
            <div className="bg-theme-secondary rounded-xl border border-border p-6 mb-5">
              <div className="flex items-center gap-2 mb-1">
                <Cloud className="h-5 w-5 text-purple-700" />
                <h2 className="text-base font-semibold text-content-primary">Microsoft Defender EASM</h2>
                {easmEnabled && (
                  <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    Active
                  </span>
                )}
              </div>
              <p className="text-sm text-content-secondary mb-4">
                Microsoft Defender External Attack Surface Management. When enabled, this organization's
                domains are automatically seeded and EASM findings (CVEs, open ports, cert issues, obsolete
                components) are pulled into Surface Scan — findings typically appear within 24 hours of
                Microsoft's crawl. By default the platform's shared EASM workspace is used; override below
                only to point this organization at its own Azure workspace.
              </p>

              <div className="flex items-center gap-2 mb-4">
                <input
                  type="checkbox"
                  id="easm-enabled"
                  checked={easmEnabled}
                  onChange={e => setEasmEnabled(e.target.checked)}
                  disabled={!isAdmin}
                  className="rounded border-gray-300 text-purple-600"
                />
                <label htmlFor="easm-enabled" className="text-sm cursor-pointer text-content-primary">
                  Enable Microsoft Defender EASM scanning for this organization's surface scan
                </label>
              </div>

              <div className="flex items-center gap-2 mb-4">
                <input
                  type="checkbox"
                  id="easm-override"
                  checked={overrideEasmWorkspace}
                  onChange={e => setOverrideEasmWorkspace(e.target.checked)}
                  disabled={!isAdmin}
                  className="rounded border-gray-300 text-purple-600"
                />
                <label htmlFor="easm-override" className="text-sm cursor-pointer text-content-primary">
                  Override EASM workspace — use this organization's own Azure workspace instead of the platform default
                </label>
              </div>

              {overrideEasmWorkspace && (
                <>
                  {!msConn?.tenantId && (
                    <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <p className="text-sm text-yellow-800">
                        No Microsoft connection configured.{" "}
                        <a href="/settings/integrations" className="font-medium underline">Set it up in Settings → Integrations</a> to use a custom EASM workspace.
                      </p>
                    </div>
                  )}

                  <div className="space-y-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-content-secondary mb-1">Resource Group</label>
                      <input
                        type="text"
                        value={easmResourceGroup}
                        onChange={e => setEasmResourceGroup(e.target.value)}
                        placeholder="e.g. axaris-easm-rg"
                        disabled={!isAdmin}
                        className="w-full max-w-lg rounded-md border border-border bg-theme-primary text-content-primary shadow-sm sm:text-sm px-3 py-2 focus:border-purple-500 focus:ring-purple-500"
                      />
                      <p className="mt-1 text-xs text-content-secondary">Azure resource group containing the EASM workspace.</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-content-secondary mb-1">Workspace Name</label>
                      <input
                        type="text"
                        value={easmWorkspaceName}
                        onChange={e => setEasmWorkspaceName(e.target.value)}
                        placeholder="e.g. axaris-easm-prod"
                        disabled={!isAdmin}
                        className="w-full max-w-lg rounded-md border border-border bg-theme-primary text-content-primary shadow-sm sm:text-sm px-3 py-2 focus:border-purple-500 focus:ring-purple-500"
                      />
                      <p className="mt-1 text-xs text-content-secondary">
                        TenantId and SubscriptionId are read from the Settings → Integrations Microsoft connection.
                      </p>
                    </div>
                  </div>
                </>
              )}

              {easmStatus && (
                <div className={`mb-4 p-3 rounded-lg border text-xs font-mono whitespace-pre-wrap break-all ${easmStatus.ok ? "bg-gray-50 border-gray-200 text-gray-700" : "bg-red-50 border-red-200 text-red-700"}`}>
                  {JSON.stringify(easmStatus, null, 2)}
                </div>
              )}

              {isAdmin && easmEnabled && (
                <button onClick={handleTestEasm} disabled={testingEasm}
                  className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">
                  {testingEasm ? <><Loader className="h-4 w-4 animate-spin" />Testing…</> : <>Test EASM Connection</>}
                </button>
              )}

              {/* Fleet-wide maintenance: runs across ALL orgs, not just this config — hence gated on
                  isAdmin (super admin) only, independent of this org's EASM toggle. */}
              {isAdmin && (
                <div className="mt-6 pt-5 border-t border-border">
                  <h4 className="text-sm font-semibold text-content-primary">Discovery-group maintenance</h4>
                  <p className="mt-1 mb-3 text-xs text-content-secondary max-w-2xl">
                    Delete Microsoft Defender EASM discovery groups no organization uses anymore. Groups for
                    domains still configured by any org (including other orgs) are kept. Runs automatically
                    each night; use this to trigger it on demand. Does not remove already-discovered assets.
                  </p>
                  <button onClick={handleCleanupOrphans} disabled={cleaningOrphans}
                    className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">
                    {cleaningOrphans
                      ? <><Loader className="h-4 w-4 animate-spin" />Cleaning up…</>
                      : <><Trash2 className="h-4 w-4" />Clean up orphaned discovery groups</>}
                  </button>

                  {orphanCleanupResult && (
                    <div className={`mt-3 p-3 rounded-lg border text-xs ${
                      orphanCleanupResult.error ? "bg-red-50 border-red-200 text-red-700"
                        : orphanCleanupResult.ran === false ? "bg-yellow-50 border-yellow-200 text-yellow-800"
                        : "bg-green-50 border-green-200 text-green-800"}`}>
                      {orphanCleanupResult.error
                        ? `Cleanup failed: ${orphanCleanupResult.error}`
                        : orphanCleanupResult.ran === false
                          ? `Cleanup did not run — ${orphanCleanupResult.skippedReason || "already in progress"}.`
                          : `Cleanup complete — deleted ${orphanCleanupResult.groupsDeleted} orphaned group(s) across ${orphanCleanupResult.workspacesScanned} workspace(s).`}
                    </div>
                  )}
                </div>
              )}
            </div>

            {isAdmin && (
              <button onClick={handleSave} disabled={saving}
                className="inline-flex items-center gap-2 px-5 py-2.5 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-purple-700 hover:bg-purple-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed">
                {saving ? <><Loader className="h-4 w-4 animate-spin" />Saving…</> : <><RefreshCw className="h-4 w-4" />Save Platform Config</>}
              </button>
            )}
          </>
        )}

        {/* ── Telegram tab ───────────────────────────────────────── */}
        {currentTab === "telegram" && (
          <>
            {/* Verification code banner */}
            {authStatus.awaitingCode && (() => {
              const isRetry = lastSubmittedRequestId !== null && authStatus.requestId !== lastSubmittedRequestId;
              const isResent = !isRetry && resendFromRequestId !== null && authStatus.requestId !== resendFromRequestId;
              const is2FA = authStatus.codeType === "2FA_password";
              return (
                <div className={`mb-5 p-4 border rounded-xl ${isRetry ? "bg-red-50 border-red-300" : "bg-yellow-50 border-yellow-300"}`}>
                  <div className="flex items-start gap-3">
                    <AlertTriangle className={`h-5 w-5 flex-shrink-0 mt-0.5 ${isRetry ? "text-red-600" : "text-yellow-600"}`} />
                    <div className="flex-1">
                      <p className={`text-sm font-semibold ${isRetry ? "text-red-800" : "text-yellow-800"}`}>
                        {isRetry
                          ? "Incorrect code — Telegram sent a new code, try again"
                          : is2FA ? "Two-Factor Authentication Required" : "Verification Code Required"}
                      </p>
                      <p className={`text-xs mt-0.5 ${isRetry ? "text-red-700" : "text-yellow-700"}`}>
                        {is2FA
                          ? "Enter your Telegram account's 2FA password to complete login."
                          : isRetry
                            ? "A new verification code was sent to your phone. Enter the latest code below."
                            : isResent
                              ? "Telegram was asked to send the code by SMS or phone call. Enter the newest code you receive. If nothing arrives, Telegram may need a few minutes before it allows another way; try again then."
                              : "Telegram sent a code to your phone or Telegram app. Enter it below to complete authentication."}
                      </p>
                      <div className="flex items-center gap-2 mt-3">
                        <input
                          type="text"
                          value={pendingCode}
                          onChange={e => setPendingCode(e.target.value)}
                          onKeyDown={e => e.key === "Enter" && handleSubmitCode()}
                          placeholder={is2FA ? "Enter 2FA password" : "Enter verification code"}
                          className={`flex-1 max-w-xs px-3 py-1.5 text-sm border rounded-lg bg-white text-gray-900 ${isRetry ? "border-red-300 focus:border-red-500 focus:ring-red-500" : "border-yellow-300 focus:border-yellow-500 focus:ring-yellow-500"}`}
                          autoFocus
                        />
                        <button onClick={handleSubmitCode} disabled={submittingCode || !pendingCode.replace(/[\s\-]/g, "")}
                          className={`inline-flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium text-white rounded-lg disabled:opacity-50 ${isRetry ? "bg-red-600 hover:bg-red-700" : "bg-yellow-600 hover:bg-yellow-700"}`}>
                          {submittingCode ? <Loader className="h-4 w-4 animate-spin" /> : "Submit"}
                        </button>
                        {!is2FA && (
                          <button onClick={handleResendCode} disabled={resendingCode || submittingCode}
                            data-testid="telegram-resend-code"
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50">
                            {resendingCode ? <Loader className="h-4 w-4 animate-spin" /> : "Send code by SMS"}
                          </button>
                        )}
                      </div>
                      {codeError && <p className="text-xs text-red-600 mt-1.5">{codeError}</p>}
                      {codeSuccess && <p className="text-xs text-green-700 mt-1.5">Code submitted — authenticating…</p>}
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Section 1 — MTProto Key Vault Credentials */}
            <div className="bg-theme-secondary rounded-xl border border-border p-6 mb-5">
              <div className="flex items-center gap-3 mb-1">
                <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-blue-100 flex-shrink-0">
                  <svg className="w-5 h-5 text-blue-600" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.894 8.221l-1.97 9.28c-.145.658-.537.818-1.084.508l-3-2.21-1.447 1.394c-.16.16-.295.295-.605.295l.213-3.053 5.56-5.023c.242-.213-.054-.333-.373-.12l-6.871 4.326-2.962-.924c-.643-.204-.657-.643.136-.953l11.57-4.461c.537-.194 1.006.131.833.941z"/>
                  </svg>
                </div>
                <div>
                  <h2 className="text-base font-semibold text-content-primary">MTProto API Credentials</h2>
                  <p className="text-xs text-content-secondary mt-0.5">Stored in Azure Key Vault — platform-wide. Required to connect the Telegram client.</p>
                </div>
                <div className="ml-auto flex items-center gap-2">
                  {authStatus.isConnected
                    ? <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700"><CheckCircle className="h-3 w-3" />Connected</span>
                    : <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-500">Disconnected</span>
                  }
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">Axaris Only</span>
                </div>
              </div>

              {tgSaved && (
                <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">
                  Telegram credentials saved to Key Vault. Telegram reconnects with them within a few seconds; if it needs a login code, the code box appears above.
                </div>
              )}
              {tgError && (
                <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{tgError}</div>
              )}

              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* API ID */}
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <label className="block text-sm font-medium text-content-secondary">API ID</label>
                    {tgApiIdSet && <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Set</span>}
                  </div>
                  <input
                    type="text"
                    value={tgApiId}
                    onChange={e => setTgApiId(e.target.value)}
                    placeholder={tgApiIdSet ? "Enter new to replace" : "e.g. 12345678"}
                    disabled={!isAdmin}
                    className="w-full rounded-md border border-border bg-theme-primary text-content-primary shadow-sm sm:text-sm px-3 py-2 focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>

                {/* API Hash */}
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <label className="block text-sm font-medium text-content-secondary">API Hash</label>
                    {tgApiHashSet && <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Set</span>}
                  </div>
                  <div className="relative">
                    <input
                      type={showTgHash ? "text" : "password"}
                      value={tgApiHash}
                      onChange={e => setTgApiHash(e.target.value)}
                      placeholder={tgApiHashSet ? "••••••••• (enter new to replace)" : "32-char hex hash"}
                      disabled={!isAdmin}
                      className="w-full rounded-md border border-border bg-theme-primary text-content-primary shadow-sm sm:text-sm px-3 py-2 pr-10 focus:border-blue-500 focus:ring-blue-500"
                    />
                    <button type="button" onClick={() => setShowTgHash(p => !p)}
                      className="absolute right-3 top-2.5 text-content-secondary hover:text-content-primary">
                      {showTgHash ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                {/* Phone Number */}
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <label className="block text-sm font-medium text-content-secondary">Phone Number</label>
                    {tgPhoneSet && <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Set</span>}
                  </div>
                  <input
                    type="text"
                    value={tgPhone}
                    onChange={e => setTgPhone(e.target.value)}
                    placeholder={tgPhoneSet ? "Enter new to replace" : "+1234567890"}
                    disabled={!isAdmin}
                    className="w-full rounded-md border border-border bg-theme-primary text-content-primary shadow-sm sm:text-sm px-3 py-2 focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>

                {/* Session Protector — AES only, DPAPI removed */}
                <div>
                  <label className="block text-sm font-medium text-content-secondary mb-1">Session Protector</label>
                  <p className="text-sm text-content-primary py-2">
                    AES-256-GCM — the only supported protector for Linux/Azure deployments.
                  </p>
                </div>

                {/* AES Session Key — read-only status (must be provisioned directly in Azure Key Vault) */}
                <div className="sm:col-span-2">
                  <div className="flex items-center gap-2 mb-1">
                    <label className="block text-sm font-medium text-content-secondary">AES Session Key</label>
                    {tgAesKeySet
                      ? <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Provisioned</span>
                      : <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">Not set</span>
                    }
                  </div>
                  <p className="text-xs text-content-secondary">
                    This key must be provisioned directly in Azure Key Vault (<code className="text-xs bg-theme-primary px-1 py-0.5 rounded">telegram-aes-session-key</code>). It cannot be set through the UI.
                  </p>
                </div>
              </div>

              {isAdmin && (
                <div className="mt-5 flex items-center gap-3 flex-wrap">
                  <button onClick={handleTelegramSave} disabled={tgSaving}
                    className="inline-flex items-center gap-2 px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed">
                    {tgSaving ? <><Loader className="h-4 w-4 animate-spin" />Saving…</> : "Save Credentials"}
                  </button>
                  <button onClick={handleClearSession} disabled={clearingSession}
                    className="inline-flex items-center gap-2 px-4 py-2 border border-red-300 text-sm font-medium rounded-md text-red-600 bg-white hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed">
                    {clearingSession ? <Loader className="h-4 w-4 animate-spin" /> : "Clear Session"}
                  </button>
                </div>
              )}
              {clearSessionMsg && (
                <p className={`mt-2 text-xs ${clearSessionMsg.ok ? "text-green-700" : "text-red-600"}`}>
                  {clearSessionMsg.text}
                </p>
              )}
            </div>

            {/* Section 2 — General / Enable + Retention */}
            <div className="bg-theme-secondary rounded-xl border border-border p-5 mb-5">
              <h2 className="text-base font-semibold text-content-primary mb-4">General</h2>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-content-primary">Monitoring enabled</p>
                  <p className="text-xs text-content-secondary mt-0.5">When disabled, the Telegram client disconnects and stops processing messages</p>
                </div>
                <button onClick={() => setIsEnabled(v => !v)} disabled={!isAdmin} className="text-blue-600 hover:text-blue-700 disabled:opacity-40">
                  {isEnabled ? <ToggleRight className="w-8 h-8" /> : <ToggleLeft className="w-8 h-8" />}
                </button>
              </div>
              <div className="mt-4 flex items-center gap-3">
                <label className="text-sm font-medium text-content-primary w-36">Retention (days)</label>
                <input
                  type="number" min={30} max={90} value={retentionDays}
                  onChange={e => setRetentionDays(Number(e.target.value))}
                  disabled={!isAdmin}
                  className="w-24 px-2 py-1.5 text-sm border border-border rounded-lg bg-theme-primary text-content-primary disabled:opacity-50"
                />
                <span className="text-xs text-content-secondary">30–90 days</span>
              </div>
            </div>

            {/* Section 3 — Allowed Channels */}
            <div className="bg-theme-secondary rounded-xl border border-border p-5 mb-5">
              <h2 className="text-base font-semibold text-content-primary mb-3">Organization Channels</h2>
              <p className="text-xs text-content-secondary mb-4">Only public channels in this list are monitored. Enter the channel username (without @).</p>
              {isAdmin && (
                <div className="flex gap-2 mb-3">
                  <input
                    type="text" placeholder="channelname" value={newChannel}
                    onChange={e => setNewChannel(e.target.value)}
                    onKeyDown={e => e.key === "Enter" && addChannel()}
                    className="flex-1 px-3 py-1.5 text-sm border border-border rounded-lg bg-theme-primary text-content-primary"
                  />
                  <button onClick={addChannel} className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                    <Plus className="w-4 h-4" /> Add
                  </button>
                </div>
              )}
              {channels.length === 0 ? (
                <p className="text-sm text-content-secondary italic">No channels configured.</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {channels.map(ch => (
                    <span key={ch} className="inline-flex items-center gap-1.5 px-3 py-1 bg-blue-50 text-blue-700 text-sm rounded-full border border-blue-200">
                      @{ch}
                      {isAdmin && (
                        <button onClick={() => removeChannel(ch)} className="hover:text-red-600 transition-colors">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Section 4 — Keywords */}
            <div className="bg-theme-secondary rounded-xl border border-border p-5 mb-5">
              <h2 className="text-base font-semibold text-content-primary mb-1">Organization Keywords</h2>
              <p className="text-xs text-content-secondary mb-4">Plain text only — no regex patterns. Keywords are matched as whole words (case-insensitive).</p>
              {isAdmin && (
                <div className="flex gap-2 mb-3">
                  <input
                    type="text" placeholder="e.g. ransomware" value={newKeyword}
                    onChange={e => setNewKeyword(e.target.value)}
                    onKeyDown={e => e.key === "Enter" && addKeyword()}
                    className="flex-1 px-3 py-1.5 text-sm border border-border rounded-lg bg-theme-primary text-content-primary"
                  />
                  <button onClick={addKeyword} className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                    <Plus className="w-4 h-4" /> Add
                  </button>
                </div>
              )}
              {keywords.length === 0 ? (
                <p className="text-sm text-content-secondary italic">No keywords configured.</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {keywords.map(kw => (
                    <span key={kw} className="inline-flex items-center gap-1.5 px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full border border-gray-200">
                      {kw}
                      {isAdmin && (
                        <button onClick={() => removeKeyword(kw)} className="hover:text-red-600 transition-colors">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Section 5 — Channel Reputation */}
            <div className="bg-theme-secondary rounded-xl border border-border p-5 mb-5">
              <h2 className="text-base font-semibold text-content-primary mb-3">Global Channels</h2>
              <p className="text-xs text-content-secondary mb-4">Global registry of known threat channels. Applies to all organizations.</p>
              {isAdmin && (
                <div className="flex flex-wrap gap-2 mb-4">
                  <input
                    type="text" placeholder="channelname" value={newRepChannel}
                    onChange={e => setNewRepChannel(e.target.value)}
                    className="flex-1 min-w-[160px] px-3 py-1.5 text-sm border border-border rounded-lg bg-theme-primary text-content-primary"
                  />
                  <select value={newRepCategory} onChange={e => setNewRepCategory(e.target.value)}
                    className="px-3 py-1.5 text-sm border border-border rounded-lg bg-theme-primary text-content-primary">
                    <option value="generic">Generic</option>
                    <option value="fraud">Fraud</option>
                    <option value="malware">Malware</option>
                    <option value="leaks">Leaks</option>
                  </select>
                  <input
                    type="text" placeholder="Notes (optional)" value={newRepNotes}
                    onChange={e => setNewRepNotes(e.target.value)}
                    className="flex-1 min-w-[160px] px-3 py-1.5 text-sm border border-border rounded-lg bg-theme-primary text-content-primary"
                  />
                  <button onClick={handleAddRepChannel} disabled={addingChannel || !newRepChannel.trim()}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
                    {addingChannel ? <Loader className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                    Add
                  </button>
                </div>
              )}
              {channelReputation.length === 0 ? (
                <p className="text-sm text-content-secondary italic">No channels in reputation list.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-2 pr-4 font-medium text-content-secondary">Channel</th>
                        <th className="text-left py-2 pr-4 font-medium text-content-secondary">Category</th>
                        <th className="text-left py-2 pr-4 font-medium text-content-secondary">Notes</th>
                        <th className="text-left py-2 font-medium text-content-secondary">Last Seen</th>
                        {isAdmin && <th />}
                      </tr>
                    </thead>
                    <tbody>
                      {channelReputation.map(c => (
                        <tr key={c.telegramChannelId} className="border-b border-border/50 hover:bg-theme-hover">
                          <td className="py-2 pr-4 text-content-primary font-medium">@{c.channelName}</td>
                          <td className="py-2 pr-4">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${categoryColor(c.riskCategory)}`}>
                              {c.riskCategory}
                            </span>
                          </td>
                          <td className="py-2 pr-4 text-content-secondary text-xs max-w-xs truncate">{c.notes || "—"}</td>
                          <td className="py-2 text-content-secondary text-xs">
                            {c.lastSeenAt ? new Date(c.lastSeenAt).toLocaleDateString() : "Never"}
                          </td>
                          {isAdmin && (
                            <td className="py-2 text-right">
                              <button onClick={() => handleDeleteRepChannel(c.telegramChannelId)} disabled={deletingId === c.telegramChannelId}
                                className="text-content-secondary hover:text-red-600 transition-colors disabled:opacity-40">
                                {deletingId === c.telegramChannelId ? <Loader className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                              </button>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Section 6 — Global Keywords */}
            <div className="bg-theme-secondary rounded-xl border border-border p-5 mb-5">
              <h2 className="text-base font-semibold text-content-primary mb-1">Global Keywords</h2>
              <p className="text-xs text-content-secondary mb-4">
                Applied to all global channels above. Only messages matching at least one keyword are distributed to organizations.
              </p>
              {isAdmin && (
                <div className="flex gap-2 mb-3">
                  <input
                    type="text" placeholder="Add keyword" value={newGlobalKeyword}
                    onChange={e => setNewGlobalKeyword(e.target.value)}
                    onKeyDown={e => { if (e.key === "Enter") addGlobalKeyword(); }}
                    className="flex-1 min-w-[160px] px-3 py-1.5 text-sm border border-border rounded-lg bg-theme-primary text-content-primary"
                  />
                  <button onClick={addGlobalKeyword} disabled={!newGlobalKeyword.trim()}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50">
                    <Plus className="w-4 h-4" />Add
                  </button>
                </div>
              )}
              {globalKeywords.length === 0 ? (
                <p className="text-sm text-content-secondary italic">No global keywords — all global channel messages are dropped.</p>
              ) : (
                <div className="flex flex-wrap gap-2 mb-3">
                  {globalKeywords.map(kw => (
                    <span key={kw} className="inline-flex items-center gap-1.5 px-3 py-1 bg-purple-50 text-purple-700 text-sm rounded-full border border-purple-200">
                      {kw}
                      {isAdmin && (
                        <button onClick={() => removeGlobalKeyword(kw)} className="hover:text-red-600 transition-colors">
                          <X className="w-3 h-3" />
                        </button>
                      )}
                    </span>
                  ))}
                </div>
              )}
              {isAdmin && (
                <div className="flex justify-end">
                  <button onClick={handleGlobalKeywordSave} disabled={globalKwSaving}
                    className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium text-sm disabled:opacity-60">
                    {globalKwSaving ? <><Loader className="w-4 h-4 animate-spin" />Saving…</> : <><Save className="w-4 h-4" />Save Global Keywords</>}
                  </button>
                </div>
              )}
            </div>

            {/* Monitoring config feedback + save */}
            {tgMonError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{tgMonError}</div>
            )}
            {tgMonSuccess && (
              <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">Monitoring configuration saved successfully.</div>
            )}
            {isAdmin && (
              <div className="flex justify-end">
                <button onClick={handleMonitoringSave} disabled={tgMonSaving}
                  className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium text-sm disabled:opacity-60">
                  {tgMonSaving ? <><Loader className="w-4 h-4 animate-spin" />Saving…</> : <><Save className="w-4 h-4" />Save Monitoring Config</>}
                </button>
              </div>
            )}
          </>
        )}

      </div>
    </div>
  );
}
